<template>
  <div class="scene-layers-menu list gap-2">
    <SceneNodeLine
      v-if="foregroundLayerNode"
      :ref="(el) => updateSceneNodeLineRef(foregroundLayerNode!.uid, el as any)"
      :node-uid="foregroundLayerNode.uid"
      :selected="sceneActiveLayerUid === foregroundLayerNode.uid"
      :editable="editable"
      @click="selectLayerNode(foregroundLayerNode.uid)"
      @contextmenu="showContextMenu($event)"
    />
    <SortableList
      :items="layerNodeEntries"
      :order="order"
      :sortable="editable"
      item-key="uid"
      ghost-list-class="scene-node-line__children-list-ghost"
      line-class="scene-node-line__child"
      @update:order="updateLayerOrder($event)"
    >
      <template #default="{ item: layerNodeEntry }">
        <SceneNodeLine
          :ref="(el) => updateSceneNodeLineRef(layerNodeEntry.uid, el as any)"
          :node-uid="layerNodeEntry.uid"
          :selected="sceneActiveLayerUid === layerNodeEntry.uid"
          :editable="editable"
          @click="selectLayerNode(layerNodeEntry.uid)"
          @contextmenu="showContextMenu($event)"
        />
      </template>
      <template #after-list v-if="editable">
        <div>
          <slot></slot>
          <ContextMenu
            v-if="contextmenuData.visible && (canDelete || contextMenuItemsUngrouped || parentSceneNode)"
            :x="contextmenuData.position.x"
            :y="contextmenuData.position.y"
            @close="contextmenuData.visible = false"
          >
            <button
              v-if="contextMenuItemsUngrouped"
              class="context-menu__entry"
              type="button"
              @click="groupSelectedItems(contextmenuData.items)"
            >
              <lucide-icon name="layers-2" class="context-menu__item-icon" />
              {{ $t("actions.group") }}
            </button>

            <button
              v-if="parentSceneNode"
              class="context-menu__entry"
              type="button"
              @click="ungroupParentGroup()"
            >
              <lucide-icon name="layers-2" class="context-menu__item-icon" />
              {{ $t("actions.ungroup") }}
            </button>
            <button
              v-if="canDelete"
              class="context-menu__entry"
              type="button"
              @click="deleteItems(contextmenuData.items)"
            >
              <lucide-icon name="trash-2" class="context-menu__item-icon" />
              {{ $t("actions.delete") }}
            </button>
          </ContextMenu>
        </div>
      </template>
    </SortableList>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive } from "vue";
import { notNull } from "@/helpers/utility/Type";
import eventHub from "@/helpers/utility/Events";

import { useModelStore } from "../model/model.store";
import { useUndo } from "@editor/store/undo/undo.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";

import ContextMenu from "@core/ui/ContextMenu.vue";
import SortableList from "@core/ui/SortableList.vue";
import SceneNodeLine from "../scene/SceneNodeLine.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import type { SceneTreeNode } from "../scene/SceneTreeItem";
import type { ItemType } from "../ItemOfStore";

import { splitUid, type Uid } from "@/helpers/utility/Id";
import { useSceneTree } from "../scene/sceneTree";
import { isLayerTreeNode } from "./sceneLayerFunctions";
import { useSceneNodesStore } from "../scene/sceneNodes.store";
import { usePresenceStore } from "@editor/store/presence.store";
import { computeMinimalOrderUpdates } from "@/helpers/utility/Sorting";
import { getAllDescendantUids, getHighestLevelNodes } from "../scene/sceneNodeFunctions";
import type { SceneNodeItem } from "../scene/SceneNodeItem";

defineProps<{
  editable: boolean;
}>();

const sceneNodeStore = useSceneNodesStore();
const modelStore = useModelStore();
const selectionStore = useSelectionStore();
const undo = useUndo();
const presence = usePresenceStore();

const sceneTree = useSceneTree();
const sceneNodeLinesRefs: Record<string, InstanceType<typeof SceneNodeLine> | null> = reactive({});
function updateSceneNodeLineRef(uid: string, line: InstanceType<typeof SceneNodeLine> | null) {
  if (line === null) {
    delete sceneNodeLinesRefs[uid];
    return;
  }
  sceneNodeLinesRefs[uid] = line;
}

// ==========================
// Selection

const sceneActiveLayerUid = computed(() => {
  return presence.activeSceneLayers[presence.activeSceneId] ?? null;
});

function selectLayerNode(uid: Uid<"sceneNode">) {
  const layerNode = sceneTree.treeNodes.get(uid);
  if (!layerNode || !isLayerTreeNode(layerNode)) return;
  if (layerNode.node.data.layerType !== "scene") return;
  presence.setActiveLayer(uid);
}

// ==========================
// Order

const layerNodes = computed(() => {
  return sceneTree.layerNodes as SceneTreeNode<"layer">[];
});
const foregroundLayerNode = computed(() => {
  return layerNodes.value.find((treeNode) => treeNode.node.data.layerType === "foreground") || null;
});
const layerNodeEntries = computed(() => layerNodes.value.map((node) => {
  return { uid: node.uid, id: node.id, type: "sceneNode", order: node.order, layerType: node.node.data.layerType };
}).filter((layer) => layer.layerType != "foreground" ));
const order = computed(() => layerNodeEntries.value.map((node) => node.uid).reverse());

function updateLayerOrder(newOrder: Uid<"sceneNode">[]) {
  const layerNodesRecord = Object.fromEntries(
    layerNodes.value.map((node) => [node.uid, { order: node.order }])
  );
  const orderReversed = newOrder.slice().reverse();
  const orderUpdates = computeMinimalOrderUpdates(
    orderReversed,
    layerNodesRecord
  );
  undo.startGroup("model", "reorderLayers");
  orderUpdates.forEach((newOrderValue, uid) => {
    const { id } = splitUid(uid);
    sceneNodeStore.update({
      id,
      order: newOrderValue
    });
  });
  undo.endGroup("model");
}

// ==========================
// Context Menu

const contextmenuData = reactive({
  items: [] as { uid: Uid<"sceneNode">; id: number; type: "sceneNode" }[],
  position: { x: 0, y: 0 },
  visible: false,
  sourceItem: null as Uid<"sceneNode"> | null,
});

function showContextMenu(
  event: MouseEvent & {
    items: { uid: Uid<"sceneNode">; id: number; type: "sceneNode" }[];
    item: Uid<"sceneNode">;
  }
) {
  if (!event.items || event.items.length === 0) return;
  contextmenuData.sourceItem = event.item;
  contextmenuData.items = event.items;
  contextmenuData.position = { x: event.clientX, y: event.clientY };
  contextmenuData.visible = true;
}

// ==========================
// Delete items

const canDelete = computed(() => {
  const nodes = contextmenuData.items;
  if (nodes.length === 0) return false;
  if (nodes.length === 1) {
    const treeNode = sceneTree.treeNodes.get(nodes[0].uid);
    if (!treeNode) return false;
    if (isLayerTreeNode(treeNode)) {
      // only scene layers can be deleted
      if (treeNode.node.data.layerType === "scene") return true;
      return false;
    }
  }
  return true;
});

async function deleteItems(items: { type: ItemType, id: number }[]) {
  contextmenuData.visible = false;
  undo.startGroup("model", "deleteItems");
  for (const item of items) {
    const store = modelStore.getStore(item.type as ItemType);
    if (!store) continue;
    await store.delete({ id: item.id });
  }
  undo.endGroup("model");
}

// ==========================
// Group / Ungroup

async function groupSelectedItems(
  items: { uid: Uid<"sceneNode">; id: number; type: "sceneNode" }[]
) {
  contextmenuData.visible = false;
  if (items.length < 2) return;
  const nodes = items.map((i) => sceneTree.treeNodes.get(i.uid)).filter(notNull);
  const group = await sceneNodeStore.group(nodes.map((i) => i.uid));
  if (!group) return;
  selectionStore.select(group);
}

function ungroupParentGroup() {
  const parentNode = parentSceneNode.value;
  if (!parentNode) return;
  contextmenuData.visible = false;
  sceneNodeStore.ungroup(parentNode);
}

const parentSceneNode = computed(() => {
  if (!contextmenuData.sourceItem) return null;
  const sourceNode = sceneTree.treeNodes.get(contextmenuData.sourceItem);
  if (!sourceNode) return null;
  if (sourceNode.node.dataType === "group") return sourceNode;

  if (!sourceNode.parentUid) return null;
  const parentNode = sceneTree.treeNodes.get(sourceNode.parentUid);
  if (!parentNode) return null;
  if (isLayerTreeNode(parentNode)) return null;
  return parentNode;
});

const contextMenuItemsUngrouped = computed(() => {
  return areItemsUngrouped(contextmenuData.items);
});

function areItemsUngrouped(items: { uid: Uid<"sceneNode"> }[]) {
  if (items.length < 2) return false;
  const nodes = items.map((i) => sceneTree.treeNodes.get(i.uid)).filter(notNull);
  const highestLevelNodes = getHighestLevelNodes(nodes);
  if (highestLevelNodes.length >= 2) return true;
  // check if ALL of the group's children are in the selection
  const nestedChildrenUids = getAllDescendantUids(highestLevelNodes[0].node);
  return nodes.some((node) => !nestedChildrenUids.has(node.uid));
}

// ==========================
// Handle new layer creation - start editing title

onMounted(() => {
  eventHub.on("sceneNode-created", onSceneNodeCreated);
});
onBeforeUnmount(() => {
  eventHub.off("sceneNode-created", onSceneNodeCreated);
});
function onSceneNodeCreated(sceneNode: SceneNodeItem) {
  if (sceneNode.dataType !== "layer") return;
  console.warn("scene node created, start editing title", sceneNode);
  nextTick(() => {
    sceneNodeLinesRefs[sceneNode.uid]?.startEditingTitle();
  });
}
</script>

<style lang="scss">
.scene-layers-menu {
  &__layer-line {
    background-color: var(--color-background-deep);
    display: grid;
    grid-template-columns: minmax(0, 1fr) 2rem;

    &:hover {
      background-color: var(--color-background);
    }
  }

  &__itemlist {
    min-height: 2rem;
  }
  &__textpreview {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    & * {
      display: inline !important;
      font-size: 1em !important;
      font-weight: 400 !important;
      padding: 0 !important;
      margin: 0 !important;
    }
    & :is(h1, h2, h3, h4, h5, h6, li) {
      padding-right: 0.5em !important;
    }
  }
}
</style>
